import type { CSSProperties } from 'react';

interface PngExportModalProps {
  isOpen: boolean;
  isExporting: boolean;
  pageCount: number;
  includeAllPages: boolean;
  onIncludeAllPagesChange: (value: boolean) => void;
  showMonochromeOption?: boolean;
  monochrome?: boolean;
  onMonochromeChange?: (value: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: CSSProperties;
}

export function PngExportModal({
  isOpen,
  isExporting,
  pageCount,
  includeAllPages,
  onIncludeAllPagesChange,
  showMonochromeOption = false,
  monochrome = false,
  onMonochromeChange,
  onClose,
  onConfirm,
  palette,
  glass,
}: PngExportModalProps) {
  if (!isOpen) return null;

  const supportsAllPages = pageCount > 1;
  const exportLabel = includeAllPages && supportsAllPages
    ? `Export ${pageCount} PNGs`
    : 'Export PNG';

  return (
    <div className="fixed inset-0 z-[520] flex items-center justify-center" onClick={isExporting ? undefined : onClose}>
      <div className="absolute inset-0 bg-black/55" />
      <div
        className="relative z-10 w-[460px] max-w-[calc(100vw-32px)] rounded-2xl border shadow-2xl overflow-hidden"
        style={{ ...glass }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: palette.panelBorder }}
        >
          <div>
            <div className="text-sm font-semibold" style={{ color: palette.panelText }}>Export PNG</div>
            <div className="text-xs mt-1" style={{ color: palette.panelSubText }}>
              Renders the full diagram offscreen, centered and high resolution.
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors text-sm disabled:opacity-50"
            style={{ color: palette.panelSubText }}
            aria-label="Close PNG export dialog"
          >
            x
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div
            className="rounded-xl border px-4 py-3 text-xs leading-relaxed"
            style={{ borderColor: palette.panelBorder, color: palette.panelSubText, background: `${palette.panelBorder}18` }}
          >
            The exported PNG ignores your current zoom and camera position. Bounds are computed from the diagram
            content so labels, fragments, groups, and lifelines are kept in frame.
          </div>

          {supportsAllPages && (
            <label
              className="flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer"
              style={{ borderColor: palette.panelBorder }}
            >
              <input
                type="checkbox"
                checked={includeAllPages}
                onChange={(event) => onIncludeAllPagesChange(event.target.checked)}
                disabled={isExporting}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium" style={{ color: palette.panelText }}>
                  Export all pages
                </div>
                <div className="text-xs mt-1" style={{ color: palette.panelSubText }}>
                  Downloads one PNG per page using each page&apos;s own content bounds.
                </div>
              </div>
            </label>
          )}

          {showMonochromeOption && (
            <label
              className="flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer"
              style={{ borderColor: palette.panelBorder }}
            >
              <input
                type="checkbox"
                checked={monochrome}
                onChange={(event) => onMonochromeChange?.(event.target.checked)}
                disabled={isExporting}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium" style={{ color: palette.panelText }}>
                  Black and white
                </div>
                <div className="text-xs mt-1" style={{ color: palette.panelSubText }}>
                  Export sequence diagrams with black strokes on white paper, ignoring accent colors.
                </div>
              </div>
            </label>
          )}
        </div>

        <div
          className="px-5 py-4 border-t flex items-center justify-end gap-2"
          style={{ borderColor: palette.panelBorder }}
        >
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50"
            style={{ borderColor: palette.panelBorder, color: palette.panelSubText }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isExporting}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60"
            style={{ background: '#6366f1', color: '#ffffff' }}
          >
            {isExporting ? 'Rendering...' : exportLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
